import { ArrowRight, ArrowLeft, Info } from "lucide-react";

export function Card({ children, className = "", variant = "default" }) {
  const variants = {
    default: "bg-surface border border-border",
    accent: "bg-accent-soft border border-accent",
    dark: "bg-ink text-bg",
  };
  return (
    <div className={`rounded-2xl p-7 md:p-8 ${variants[variant]} ${className}`}>
      {children}
    </div>
  );
}

export function Label({ num, children }) {
  return (
    <div className="mb-3">
      {num && (
        <span className="font-mono text-xs text-accent mr-2.5">{num}</span>
      )}
      <span className="text-base font-semibold text-ink">{children}</span>
    </div>
  );
}

export function Helper({ children }) {
  return (
    <p className="text-[13px] text-ink-soft leading-relaxed mt-2.5 mb-0">
      {children}
    </p>
  );
}

export function YenInput({ value, onChange, placeholder = "0" }) {
  return (
    <div className="flex items-center border-2 border-border rounded-xl px-4 bg-bg focus-within:border-accent transition-colors">
      <span className="font-serif text-3xl text-ink-soft mr-2">¥</span>
      <input
        type="number"
        value={value || ""}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        placeholder={placeholder}
        className="flex-1 text-[28px] font-medium font-serif text-ink py-4 bg-transparent outline-none w-full"
      />
    </div>
  );
}

export function BrlInput({ value, onChange, placeholder = "0" }) {
  return (
    <div className="flex items-center border-2 border-border rounded-xl px-4 bg-bg focus-within:border-accent transition-colors">
      <span className="font-serif text-2xl text-ink-soft mr-2">R$</span>
      <input
        type="number"
        value={value || ""}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
        placeholder={placeholder}
        className="flex-1 text-[24px] font-medium font-serif text-ink py-4 bg-transparent outline-none w-full"
      />
    </div>
  );
}

export function SegmentedControl({ value, onChange, options }) {
  return (
    <div
      className="grid gap-2 p-1 bg-bg rounded-xl border-2 border-border"
      style={{ gridTemplateColumns: `repeat(${options.length}, 1fr)` }}
    >
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`py-3 px-2 rounded-lg font-semibold text-sm transition-all ${
              active ? "bg-ink text-bg" : "bg-transparent text-ink hover:bg-border/50"
            }`}
          >
            <div>{opt.label}</div>
            {opt.sub && (
              <div className="text-[11px] opacity-70 mt-0.5 font-normal">
                {opt.sub}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function RangeWithValue({ min, max, value, onChange, format, step = 1 }) {
  return (
    <div>
      <div className="font-serif text-3xl md:text-4xl font-medium text-ink mb-3">
        {format(value)}
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full cursor-pointer"
      />
      <div className="flex justify-between text-[11px] text-ink-soft mt-1 font-mono">
        <span>{format(min)}</span>
        <span>{format(max)}</span>
      </div>
    </div>
  );
}

export function CheckboxRow({ checked, onChange, label, helper }) {
  return (
    <label className="flex gap-3 items-start cursor-pointer group">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 w-5 h-5 accent-accent cursor-pointer"
      />
      <div>
        <div className="font-medium text-ink">{label}</div>
        {helper && (
          <div className="text-[13px] text-ink-soft mt-1 leading-relaxed">
            {helper}
          </div>
        )}
      </div>
    </label>
  );
}

export function NextButton({ onClick, children, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex items-center justify-center gap-2.5 bg-ink text-bg py-4 px-8 rounded-xl font-semibold text-base w-full hover:bg-ink/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
    >
      {children}
      {typeof children === "string" && <ArrowRight size={18} />}
    </button>
  );
}

export function SecondaryButton({ onClick, children, as: As = "button", ...rest }) {
  return (
    <As
      onClick={onClick}
      className="flex items-center justify-center gap-2.5 bg-transparent text-ink py-3.5 px-6 rounded-xl font-medium text-[15px] border border-border hover:bg-surface transition-colors w-full no-underline"
      {...rest}
    >
      {children}
    </As>
  );
}

export function BackLink({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 bg-transparent border-0 text-ink-soft text-sm mb-5 cursor-pointer p-0 hover:text-ink transition-colors"
    >
      <ArrowLeft size={14} />
      Voltar
    </button>
  );
}

export function SectionTitle({ icon, children }) {
  return (
    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-ink-soft mb-4 font-semibold">
      {icon}
      {children}
    </div>
  );
}

export function BreakdownRow({ label, value, bold }) {
  return (
    <div
      className={`flex justify-between items-baseline py-3 ${
        bold ? "border-t border-border mt-2" : ""
      }`}
    >
      <span
        className={`text-sm ${
          bold ? "text-ink font-semibold" : "text-ink-soft"
        }`}
      >
        {label}
      </span>
      <span
        className={`font-mono text-ink tabular-nums ${
          bold ? "text-lg font-semibold" : "text-base font-medium"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

export function ProgressDots({ current, total }) {
  return (
    <div className="flex justify-center gap-2 mt-8">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-2 rounded transition-all ${
            i === current
              ? "w-6 bg-accent"
              : i < current
              ? "w-2 bg-ink"
              : "w-2 bg-border"
          }`}
        />
      ))}
    </div>
  );
}

export function Eyebrow({ children }) {
  return (
    <div className="text-[11px] tracking-[0.18em] uppercase text-accent mb-4 font-semibold">
      {children}
    </div>
  );
}

export function PageTitle({ children, italicHighlight }) {
  return (
    <h1 className="font-serif font-medium text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-5">
      {children}
    </h1>
  );
}

export function Disclaimer({ children }) {
  return (
    <div className="mt-8 p-5 border border-dashed border-border rounded-xl text-[13px] leading-relaxed text-ink-soft flex gap-3">
      <Info size={16} className="shrink-0 mt-0.5" />
      <div>{children}</div>
    </div>
  );
}
